import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { AppConfig } from '../types';

/**
 * Validates and loads application configuration from environment variables
 * Throws error if required variables are missing
 */
function loadConfig(): AppConfig {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  const lemonSqueezyStoreId = import.meta.env.VITE_LEMON_SQUEEZY_STORE_ID;
  const lemonSqueezyProductId = import.meta.env.VITE_LEMON_SQUEEZY_PRODUCT_ID;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error(
      'Missing required Supabase environment variables. Please check your .env file.'
    );
  }

  return {
    supabase: {
      url: supabaseUrl,
      anonKey: supabaseAnonKey,
    },
    lemonSqueezy: {
      storeId: lemonSqueezyStoreId || '',
      productId: lemonSqueezyProductId || '',
    },
  };
}

// Load configuration
export const config = loadConfig();

/**
 * Supabase client instance
 * Used for authentication, database queries, and realtime subscriptions
 */
export const supabase: SupabaseClient = createClient(
  config.supabase.url,
  config.supabase.anonKey,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
    },
  }
);

/**
 * Database schema types for better TypeScript support
 */
export interface Database {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string;
          email: string;
          tier: 'free' | 'signed_in' | 'subscribed';
          subscription_id: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id: string;
          email: string;
          tier?: 'free' | 'signed_in' | 'subscribed';
          subscription_id?: string | null;
        };
        Update: {
          tier?: 'free' | 'signed_in' | 'subscribed';
          subscription_id?: string | null;
        };
      };
      file_share_history: {
        Row: {
          id: string;
          user_id: string | null;
          filename: string;
          file_size: number;
          peer_id: string;
          peer_name: string | null;
          transfer_type: 'sent' | 'received';
          created_at: string;
        };
        Insert: {
          user_id: string | null;
          filename: string;
          file_size: number;
          peer_id: string;
          peer_name?: string | null;
          transfer_type: 'sent' | 'received';
        };
      };
    };
  };
}
